import {type ChartAggOption} from '../../../generated/graphql';
import {type PrimaryLineType} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {buildTimePoint, extendPoint, type PointWithMeta} from './buildPoints';
import {
  type HistoryRecord,
  type LineAggregation,
  type ValidHistoryKey,
} from './types';
import {
  getAreaSafeYKeys,
  getSafeXKey,
  getSafeYKey,
  sortPointsByX,
  takeLastDuplicate,
} from './util';

/**
 * Builds the (x, y) plot points for one run's line from its bucketed history.
 *
 * Each HistoryRecord is a "bucket" — a span of logged steps reduced to summary stats
 * (Min/Max/Avg/Last, plus optional XAtMin/XAtMax: the x-position where the min/max
 * occurred). The output shape depends on config.agg:
 *
 *   - 'Avg': a single line. Usually one point per bucket at its last x/value; in
 *     min-max-last mode each bucket instead expands into its low/high/last anchors
 *     — (xAtMin, yMin), (xAtMax, yMax), (xLast, yLast) — so one line shows the spread
 *     without a separate shaded band.
 *   - otherwise: a band line — per bucket a {x, y, y0} pair (min/max, or avg ± stdDev/
 *     stdErr) used to shade a region.
 *
 * Buckets with non-finite y (NaN/±Inf) go to nanPoints so the caller can mark them.
 * Results are sorted by x and de-duplicated to the last value at each x — except on the
 * _step axis, which the API already returns ordered.
 */
export function parseHistory(
  history: HistoryRecord[],
  metric: string,
  xMetric: string,
  otherMetrics: string[],
  config: {
    agg: LineAggregation;
    startTime: {
      value: number;
      unit: 'seconds' | 'milliseconds';
    };
    usingExpressions: boolean; // we don't need to extend the point values if we're not computing any expressions
    primaryLineType?: PrimaryLineType; // when 'min-max-last', expand each bucket into 3 anchors
  },
  // Group aggregation (mean/min/max/last) — picks which per-bucket stat to read as y for grouped runs.
  groupAgg?: ChartAggOption
) {
  const finitePoints: PointWithMeta[] = [];
  const nanPoints: PointWithMeta[] = [];

  if (history.length < 1) {
    return {
      finitePoints: [],
      nanPoints: [],
    };
  }

  /* We need to extend the point with all metrics so we can compute expressions later on. This extension won't include the y-axis metric because:
  a) if we include it we might overwrite the yMin value;
  b) we can't compute y-expressions anyway because we can only do that from the output of built lines. */
  const xMetrics = otherMetrics.concat(xMetric);

  const xKey = getSafeXKey({
    xMetricKey: xMetric,
    record: history[0],
    position: 'last',
  });
  if (config.agg === 'Avg') {
    const yKey = getSafeYKey({
      yMetricKey: metric,
      record: history[0],
      aggType: groupAgg,
    });
    /* Min-max-last mode: expand each bucket into low/high/last anchors so a single line
       shows the spread. The backend tags buckets with {metric}XAtMin/XAtMax (the x where
       the min/max occurred) when TrackXAtMinMax is on. Skipped under expressions: those are
       evaluated against bucket-level metrics that only line up with the xLast anchor. */
    const xAtMinKey = `${metric}XAtMin` as ValidHistoryKey;
    const xAtMaxKey = `${metric}XAtMax` as ValidHistoryKey;
    const yMinKey = `${metric}Min` as ValidHistoryKey;
    const yMaxKey = `${metric}Max` as ValidHistoryKey;
    const hasNaNKey = `${metric}HasNaN` as ValidHistoryKey;

    /* Decide expansion once for the whole line from the caller's primaryLineType — not from
       history[0]'s keys, since the first bucket can legitimately lack XAtMin/XAtMax. Buckets
       that lack them still fall back individually in the per-record guard below. */
    const tryExpand =
      !config.usingExpressions && config.primaryLineType === 'min-max-last';
    /* Put XAtMin/XAtMax on the same axis as xLast. They arrive as raw _timestamp (Unix epoch)
       seconds; buildTimePoint only rebases the base _timestamp* keys behind xLast, never these.
       So on the _absolute_runtime axis subtract the run start time; on every other axis they
       already match xLast, so the shift is zero. */
    const startTimeSeconds =
      config.startTime.unit === 'seconds'
        ? config.startTime.value
        : config.startTime.value / 1000;
    const xAtShift = xMetric === '_absolute_runtime' ? startTimeSeconds : 0;
    for (const historyP of history) {
      if (xMetric === '_absolute_runtime') {
        buildTimePoint(historyP, config.startTime);
      }
      const lastX = Number(historyP[xKey]);
      if (!isFinite(lastX)) {
        continue;
      }
      const lastY = Number(historyP[yKey]);

      if (tryExpand) {
        /* Only expand buckets that carry numeric XAtMin/XAtMax. Single-point buckets (min==max)
           omit them, and a non-finite x serializes to a "NaN"/"Infinity" string — both fail this
           guard and drop to the one-point path below. Shift a local copy, never historyP itself:
           parseHistory runs twice over the same cached buckets (primary then smoothed metric), so
           mutating in place would subtract the start time twice (cf. the same idempotency guard in
           convertTimestampToAbsoluteRuntime, run_line_helpers.go). */
        const xAtMinRaw = historyP[xAtMinKey];
        const xAtMaxRaw = historyP[xAtMaxKey];
        if (
          typeof xAtMinRaw === 'number' &&
          typeof xAtMaxRaw === 'number' &&
          isFinite(xAtMinRaw) &&
          isFinite(xAtMaxRaw)
        ) {
          const xAtMin = xAtMinRaw - xAtShift;
          const xAtMax = xAtMaxRaw - xAtShift;
          /* yMin/yMax can be non-finite even when XAtMin/XAtMax are present: the backend emits
             Min/Max as "Infinity"/"-Infinity" (NegInf/PosInf) independently of x-tracking, so a
             bucket mixing finite and ±Inf samples carries a finite XAt but a non-finite extreme.
             Coerce and route non-finite extremes to nanPoints, mirroring the xLast/yLast path below. */
          const yMin = Number(historyP[yMinKey]);
          const yMax = Number(historyP[yMaxKey]);
          const yMinFinite = isFinite(yMin);
          const yMaxFinite = isFinite(yMax);
          /* Emit the two anchors in x order; xLast is always rightmost, so it's the third point. */
          if (xAtMin <= xAtMax) {
            (yMinFinite ? finitePoints : nanPoints).push({x: xAtMin, y: yMin});
            (yMaxFinite ? finitePoints : nanPoints).push({x: xAtMax, y: yMax});
          } else {
            (yMaxFinite ? finitePoints : nanPoints).push({x: xAtMax, y: yMax});
            (yMinFinite ? finitePoints : nanPoints).push({x: xAtMin, y: yMin});
          }
          const lastYFinite = isFinite(lastY);
          if (lastYFinite) {
            finitePoints.push({x: lastX, y: lastY});
          } else {
            nanPoints.push({x: lastX, y: lastY});
          }
          /* One NaN marker per bucket, at xLast — same place the one-point path puts it. */
          if (lastYFinite && historyP[hasNaNKey]) {
            nanPoints.push({x: lastX, y: NaN});
          }
          continue;
        }
        /* Fall through: single-point bucket without XAtMin/XAtMax (the backend skips emitting them when min==max), or non-finite custom x-axis value. Legacy 1-point/bin handling below covers both. */
      }

      const point = {x: lastX, y: lastY};
      if (config.usingExpressions) {
        extendPoint(point, historyP, xMetrics, groupAgg);
      }
      if (isFinite(point.y)) {
        finitePoints.push(point);

        if (historyP[hasNaNKey]) {
          nanPoints.push({...point, y: NaN});
        }
      } else {
        nanPoints.push(point);
      }
    }
  } else {
    const {minKey, maxKey, avgKey, stdDevKey, stdErrKey} = getAreaSafeYKeys(
      metric,
      history[0]
    );
    for (const historyP of history) {
      if (xMetric === '_absolute_runtime') {
        buildTimePoint(historyP, config.startTime);
      }
      let v;
      if (config.agg === 'stdDev') {
        v = {
          x: Number(historyP[xKey]),
          y: Number(historyP[avgKey]) - Number(historyP[stdDevKey]),
          y0: Number(historyP[avgKey]) + Number(historyP[stdDevKey]),
        };
      } else if (config.agg === 'stdErr') {
        v = {
          x: Number(historyP[xKey]),
          y: Number(historyP[avgKey]) - Number(historyP[stdErrKey]),
          y0: Number(historyP[avgKey]) + Number(historyP[stdErrKey]),
        };
      } else {
        v = {
          x: Number(historyP[xKey]),
          y: Number(historyP[minKey]),
          y0: Number(historyP[maxKey]),
        };
      }
      if (config.usingExpressions) {
        extendPoint(v, historyP, xMetrics, groupAgg);
      }

      /* Single-point buckets contribute nothing to a band — the primary line already covers
         them — so skip them when their min/max isn't finite. */
      if (historyP.bucketSize === 1 && (!isFinite(v.y) || !isFinite(v.y0))) {
        continue;
      }

      /* Keep NaN out of the band (the primary line shows it). A bucket of 2+ points whose min
         and max are both non-finite must be an -Inf/+Inf pair, which we keep so the band still
         renders a range. */
      const hasANaNpoint = isNaN(v.y) || isNaN(v.y0);
      const hasAFinitePoint = isFinite(v.y) || isFinite(v.y0);
      const pointsAreEqual = v.y === v.y0;
      if (!hasANaNpoint && (hasAFinitePoint || !pointsAreEqual)) {
        finitePoints.push(v);
      }
    }
  }

  /* Custom x-axes (anything but _step) aren't guaranteed ordered, and min-max-last expansion
     can place several anchors at the same x. Sort by x and keep the last point at each x
     ("last log wins"). _step is already ordered by the API, so leave it untouched. */
  const isPlottedAgainstStep = xMetric === '_step';

  return {
    finitePoints: isPlottedAgainstStep
      ? finitePoints
      : finitePoints.sort(sortPointsByX).filter(takeLastDuplicate),
    nanPoints: isPlottedAgainstStep
      ? nanPoints
      : nanPoints.sort(sortPointsByX).filter(takeLastDuplicate),
  };
}
